import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  Chip,
  Card,
  CardContent,
  CardHeader,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import config from '../config';

function PlanogramManagement() {
  const { token } = useAuth();
  const [planograms, setPlanograms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ message: '', type: 'info' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlanogram, setSelectedPlanogram] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadData, setUploadData] = useState({
    description: ''
  });

  // Load planograms
  const loadPlanograms = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${config.apiUrl}/planograms`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPlanograms(data);
      } else {
        setStatus({ message: 'Failed to load planograms', type: 'error' });
      }
    } catch (error) {
      setStatus({ message: 'Error loading planograms: ' + error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlanograms();
  }, []);

  // Handle CSV upload
  const handleFileUpload = async () => {
    if (!uploadFile) {
      setStatus({ message: 'Please select a CSV file', type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('description', uploadData.description);

      const response = await fetch(`${config.apiUrl}/planograms/upload-master`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        setStatus({ message: `Master planogram data uploaded successfully! ${result.planogramCount} planograms with ${result.totalBins} bins processed.`, type: 'success' });
        setShowUploadDialog(false);
        setUploadData({ description: '' });
        setUploadFile(null);
        loadPlanograms();
      } else {
        const error = await response.json();
        setStatus({ message: error.error || 'Upload failed', type: 'error' });
      }
    } catch (error) {
      setStatus({ message: 'Upload error: ' + error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Delete planogram
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this planogram? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${config.apiUrl}/planograms/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setStatus({ message: 'Planogram deleted successfully', type: 'success' });
        loadPlanograms();
      } else {
        setStatus({ message: 'Failed to delete planogram', type: 'error' });
      }
    } catch (error) {
      setStatus({ message: 'Delete error: ' + error.message, type: 'error' });
    }
  };

  // Filter planograms based on search term
  const filteredPlanograms = planograms.filter(planogram =>
    planogram.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    planogram.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
    planogram.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentPlanograms = filteredPlanograms.slice(startIndex, endIndex);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Planogram Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<UploadIcon />}
          onClick={() => setShowUploadDialog(true)}
          color="warning"
        >
          Upload Master Data
        </Button>
      </Box>

      <Alert severity="warning" sx={{ mb: 3 }}>
        <strong>Warning:</strong> Uploading master data will replace ALL existing planogram data. 
        Make sure your CSV contains all current planograms and bins before uploading.
      </Alert>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload a single master CSV file containing all planograms and bins. This replaces all existing data and makes it available for bin auditing.
      </Typography>

      {status.message && (
        <Alert severity={status.type} sx={{ mb: 3 }}>
          {status.message}
        </Alert>
      )}

      {/* Search and Stats */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search Planograms"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={`Total: ${planograms.length}`} color="primary" />
                <Chip label={`Filtered: ${filteredPlanograms.length}`} color="secondary" />
                <Chip 
                  label={`Total Bins: ${planograms.reduce((sum, p) => sum + (p.bins?.length || 0), 0)}`} 
                  color="success" 
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Planograms Table */}
      <Card>
        <CardHeader title="Planograms" />
        <CardContent>
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Customer</strong></TableCell>
                  <TableCell><strong>Location</strong></TableCell>
                  <TableCell><strong>Bin Count</strong></TableCell>
                  <TableCell><strong>Uploaded By</strong></TableCell>
                  <TableCell><strong>Uploaded At</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentPlanograms.map((planogram) => (
                  <TableRow key={planogram._id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {planogram.name}
                      </Typography>
                      {planogram.description && (
                        <Typography variant="caption" color="text.secondary">
                          {planogram.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{planogram.customer}</TableCell>
                    <TableCell>{planogram.location}</TableCell>
                    <TableCell>
                      <Chip 
                        label={planogram.bins?.length || 0} 
                        color="primary" 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      {planogram.uploadedBy?.firstName} {planogram.uploadedBy?.lastName}
                    </TableCell>
                    <TableCell>
                      {new Date(planogram.uploadedAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(planogram._id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredPlanograms.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onClose={() => setShowUploadDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Upload Master Planogram Data</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description (Optional)"
                value={uploadData.description}
                onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                placeholder="e.g., Updated planogram data for Q1 2024"
              />
            </Grid>
            <Grid item xs={12}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Master CSV File *
                </Typography>
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  style={{ width: '100%' }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Upload a CSV file containing ALL planograms and bins. This will replace all existing planogram data.
                  <br /><br />
                  <strong>Required columns:</strong> Customer Number, Location Name, Planogram Name, 
                  Planogram ID, Bin Device Part Number, Fastenal Part #, Customer Part #, 
                  SKU Description, Bin Row, Bin Column, Sequence, Min Qty, Max Qty, Reorder Qty, SLN Position ID
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUploadDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleFileUpload} 
            variant="contained"
            disabled={loading || !uploadFile}
            color="warning"
          >
            {loading ? 'Uploading...' : 'Replace All Data'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PlanogramManagement;
